import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  AlertCircle, 
  Search,
  Filter,
  Users,
  Play,
  X,
  ArrowUpDown,
  TrendingDown,
  Target,
  Zap,
  Calendar as CalIcon,
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";
import AlertRichiamoDialog from "../components/AlertRichiamoDialog";
import { etichettaTask } from "../utils/etichettaTask";

const TASK_TYPE_LABELS = {
  call_strategica: "Call Strategica",
  script_videomaker: "Script Videomaker",
  script_cliente: "Script Cliente",
  conferma_script: "Conferma Script",
  ped_programmazione: "PED + Programmazione",
  avvio_adv: "Avvio ADV",
  appuntamento_successivo: "Appuntamento Successivo",
  report_fine_mese: "Report Fine Mese",
  editing_video: "Editing Video",
  allineamento_strategico_tiktok: "Allineamento Strategico (TikTok)",
  call_settimanale_tiktok: "Call Settimanale (TikTok)",
  check_adv_tiktok: "Check ADV (TikTok)",
  check_affiliazioni_live_tiktok: "Check Affiliazioni e Live (TikTok)"
};

const SEMAFORO_CONFIG = {
  verde: { label: "In Tempo", color: "bg-emerald-500", icon: CheckCircle2 },
  giallo: { label: "In Scadenza", color: "bg-amber-500", icon: Clock },
  rosso: { label: "Urgente", color: "bg-red-500", icon: AlertTriangle },
  scaduto: { label: "Scaduto", color: "bg-zinc-700", icon: AlertCircle }
};

const STATUS_LABELS = {
  da_fare: "Da fare",
  in_corso: "In corso",
  completato: "Completato"
};

export default function TasksPage({ user }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [tasks, setTasks] = useState([]);
  const [smmUsers, setSmmUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [filterSmm, setFilterSmm] = useState(searchParams.get("smm") || "all");
  const [filterSemaforo, setFilterSemaforo] = useState(searchParams.get("semaforo") || "all");
  const [filterStatus, setFilterStatus] = useState(searchParams.get("status") || "all");
  const [filterTaskType, setFilterTaskType] = useState("all");
  const [sortBy, setSortBy] = useState("due_date");
  
  const isDirettore = user?.role === "direttore_tecnico" || user?.role === "direttore_commerciale";
  const isPM = user?.role === "pm" || isDirettore || (user?.secondary_roles || []).includes("pm");
  const [cpanel, setCpanel] = useState(null);

  // Alert dialog state per criticità
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertContext, setAlertContext] = useState(null); // {clientId, clientName, source, sourceRef, sourceLabel, prefill}

  const openAlertForCriticality = (clientId, clientName, kind, data) => {
    let sourceLabel = "";
    let prefill = "";
    if (kind === "cpr") {
      sourceLabel = `CPR alto: €${data.cpr.toFixed(2)} (soglia €${data.threshold.toFixed(2)})`;
      prefill = `Segnalo criticità marketing su ${clientName}:\n\n- CPR attuale: €${data.cpr.toFixed(2)}\n- Soglia definita: €${data.threshold.toFixed(2)}\n- Spesa periodo: €${data.spend.toFixed(2)}\n- Leads: ${data.leads}\n\nProblema riscontrato: `;
    } else if (kind === "cac") {
      sourceLabel = `CAC alto: €${data.cac.toFixed(2)} (soglia €${data.threshold.toFixed(2)})`;
      prefill = `Segnalo criticità marketing su ${clientName}:\n\n- CAC attuale: €${data.cac.toFixed(2)}\n- Soglia definita: €${data.threshold.toFixed(2)}\n- Spesa periodo: €${data.spend.toFixed(2)}\n- Appuntamenti: ${data.appointments}\n\nProblema riscontrato: `;
    } else if (kind === "views") {
      sourceLabel = `Views calate ${Math.abs(data.delta_pct).toFixed(1)}%`;
      prefill = `Segnalo calo di performance organic su ${clientName}:\n\n- Views periodo: ${data.current}\n- Views periodo precedente: ${data.previous}\n- Delta: ${data.delta_pct.toFixed(1)}%\n\nProblema riscontrato: `;
    }
    setAlertContext({
      clientId, clientName,
      source: "criticality",
      sourceRef: `${kind}:${clientId}`,
      sourceLabel,
      prefill,
    });
    setAlertOpen(true);
  };

  useEffect(() => {
    fetchData();
    // Carica overview del controllo di gestione (task counts + criticals marketing)
    (async () => {
      try {
        const r = await apiFetch("/control-panel/overview");
        if (r.ok) setCpanel(await r.json());
      } catch (e) { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    // Update URL params when filters change
    const params = new URLSearchParams();
    if (filterSmm !== "all") params.set("smm", filterSmm);
    if (filterSemaforo !== "all") params.set("semaforo", filterSemaforo);
    if (filterStatus !== "all") params.set("status", filterStatus);
    setSearchParams(params, { replace: true });
  }, [filterSmm, filterSemaforo, filterStatus, setSearchParams]);

  const fetchData = async () => {
    try {
      const [tasksRes, smmRes, clientsRes] = await Promise.all([
        apiFetch("/tasks"),
        apiFetch("/users/smm"),
        apiFetch("/clients")
      ]);

      if (tasksRes.ok) setTasks(await tasksRes.json());
      if (smmRes.ok) setSmmUsers(await smmRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const response = await apiFetch(`/tasks/${taskId}/status?status=${newStatus}`, {
        method: "PATCH"
      });

      if (response.ok) {
        toast.success("Stato aggiornato");
        fetchData();
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  // Build client to SMM map
  const clientSmmMap = {};
  clients.forEach(c => {
    clientSmmMap[c.client_id] = c.assigned_smm_id;
  });

  const getSmmName = (smmId) => {
    const smm = smmUsers.find(u => u.user_id === smmId);
    return smm?.name || "Non assegnato";
  };

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    // Search filter
    if (searchQuery && !task.client_name?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // SMM filter (for PM only)
    if (isPM && filterSmm !== "all") {
      const taskSmmId = clientSmmMap[task.client_id];
      if (filterSmm === "unassigned") {
        if (taskSmmId) return false;
      } else {
        if (taskSmmId !== filterSmm) return false;
      }
    }
    
    // Semaforo filter
    if (filterSemaforo !== "all" && task.semaforo !== filterSemaforo) {
      return false;
    }
    
    // Task completati NASCOSTI di default dalla lista (restano in DB come storico).
    // Solo un filtro esplicito status=completato può mostrarli (nel caso serva vedere lo storico).
    if (task.status === "completato" && filterStatus !== "completato") {
      return false;
    }

    // Status filter
    if (filterStatus !== "all" && task.status !== filterStatus) {
      return false;
    }
    
    // Task type filter
    if (filterTaskType !== "all" && task.task_type !== filterTaskType) {
      return false;
    }
    
    return true;
  });

  // Sort tasks
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    if (sortBy === "due_date") {
      return new Date(a.due_date) - new Date(b.due_date);
    }
    if (sortBy === "client") {
      return (a.client_name || "").localeCompare(b.client_name || "");
    }
    if (sortBy === "semaforo") {
      const order = { scaduto: 0, rosso: 1, giallo: 2, verde: 3 };
      return (order[a.semaforo] || 4) - (order[b.semaforo] || 4);
    }
    return 0;
  });

  // Stats calcolati (usati in altri punti come contatori dei filtri, non più visualizzati nel pannello)
  const stats = {
    verde: filteredTasks.filter(t => t.semaforo === "verde").length,
    giallo: filteredTasks.filter(t => t.semaforo === "giallo").length,
    rosso: filteredTasks.filter(t => t.semaforo === "rosso").length,
    scaduto: filteredTasks.filter(t => t.semaforo === "scaduto").length,
  };

  const clearFilters = () => {
    setSearchQuery("");
    setFilterSmm("all");
    setFilterSemaforo("all");
    setFilterStatus("all");
    setFilterTaskType("all");
  };

  const hasActiveFilters = filterSmm !== "all" || filterSemaforo !== "all" || 
                           filterStatus !== "all" || filterTaskType !== "all" || searchQuery;

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-48"></div>
        <div className="h-24 bg-muted rounded-xl"></div>
        <div className="h-64 bg-muted rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
            Controllo di Gestione
          </h1>
          <p className="text-muted-foreground mt-1 flex items-center gap-2">
            <CalIcon className="w-4 h-4" />
            <span>
              Da monitorare oggi · <b className="text-slate-800">{new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</b>
            </span>
          </p>
        </div>
      </div>

      {/* Task counters cliccabili (Urgenti / In scadenza a breve / Scaduti) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button
          type="button"
          onClick={() => { setFilterSemaforo("rosso"); setFilterStatus("all"); document.getElementById("tasks-list-anchor")?.scrollIntoView({ behavior: "smooth" }); }}
          className="text-left"
          data-testid="cp-tile-urgent"
        >
          <Card className={`border-red-200 bg-red-50/40 hover:shadow-md transition-shadow ${filterSemaforo === "rosso" ? "ring-2 ring-red-500" : ""}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 border border-red-200 flex items-center justify-center">
                <Zap className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <div className="text-[11px] uppercase font-semibold text-red-700 tracking-wide">Task Urgenti · in scadenza oggi</div>
                <div className="text-3xl font-bold text-red-800 leading-none mt-0.5">{cpanel?.task_summary?.urgent ?? "—"}</div>
              </div>
            </CardContent>
          </Card>
        </button>
        <button
          type="button"
          onClick={() => { setFilterSemaforo("giallo"); setFilterStatus("all"); document.getElementById("tasks-list-anchor")?.scrollIntoView({ behavior: "smooth" }); }}
          className="text-left"
          data-testid="cp-tile-due-today"
        >
          <Card className={`border-amber-200 bg-amber-50/40 hover:shadow-md transition-shadow ${filterSemaforo === "giallo" ? "ring-2 ring-amber-500" : ""}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <div className="text-[11px] uppercase font-semibold text-amber-700 tracking-wide">In scadenza a breve</div>
                <div className="text-3xl font-bold text-amber-800 leading-none mt-0.5">{cpanel?.task_summary?.due_today ?? "—"}</div>
              </div>
            </CardContent>
          </Card>
        </button>
        <button
          type="button"
          onClick={() => { setFilterSemaforo("scaduto"); setFilterStatus("all"); document.getElementById("tasks-list-anchor")?.scrollIntoView({ behavior: "smooth" }); }}
          className="text-left"
          data-testid="cp-tile-overdue"
        >
          <Card className={`border-slate-300 bg-slate-100/60 hover:shadow-md transition-shadow ${filterSemaforo === "scaduto" ? "ring-2 ring-slate-500" : ""}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-200 border border-slate-300 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-slate-700" />
              </div>
              <div className="flex-1">
                <div className="text-[11px] uppercase font-semibold text-slate-700 tracking-wide">Task Scaduti</div>
                <div className="text-3xl font-bold text-slate-800 leading-none mt-0.5">{cpanel?.task_summary?.overdue ?? "—"}</div>
              </div>
            </CardContent>
          </Card>
        </button>
      </div>

      {/* Criticità Performance Marketing */}
      {cpanel && (
        <Card className="border border-rose-200 bg-gradient-to-br from-rose-50/50 to-white" data-testid="cp-criticals">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-rose-800">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span className="uppercase text-[11px] sm:text-sm tracking-wide">Criticità · Performance Marketing</span>
              </div>
              <span className="sm:ml-auto text-[11px] font-normal text-slate-500">
                {cpanel.scope?.clients_analyzed ?? 0} clienti · ultimi 30gg
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            {(cpanel.criticals?.cpr || []).map((a, i) => (
              <div key={"cpr-"+i} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 py-2 px-2 rounded-md bg-white/70 border border-rose-100" data-testid={`cp-cpr-${i}`}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Target className="w-4 h-4 text-rose-600 flex-shrink-0" />
                  <span className="font-semibold text-rose-900 truncate">ATTENZIONE {a.name}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap text-xs sm:text-sm">
                  <span className="text-slate-600">CPR:</span>
                  <span className="font-mono font-bold text-rose-700">€{a.cpr.toFixed(2)}</span>
                  <span className="text-slate-500 text-[11px] sm:text-xs">(soglia €{a.threshold.toFixed(2)} · {a.leads} lead · €{a.spend.toFixed(2)} spesa)</span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-1 h-6 text-[11px] border-red-300 text-red-700 hover:bg-red-50"
                    onClick={() => openAlertForCriticality(a.client_id, a.name, "cpr", a)}
                    data-testid={`alert-cpr-${i}`}
                  >
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Segnala
                  </Button>
                </div>
              </div>
            ))}
            {(cpanel.criticals?.cac || []).map((a, i) => (
              <div key={"cac-"+i} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 py-2 px-2 rounded-md bg-white/70 border border-orange-100" data-testid={`cp-cac-${i}`}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Target className="w-4 h-4 text-orange-600 flex-shrink-0" />
                  <span className="font-semibold text-orange-900 truncate">ATTENZIONE {a.name}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap text-xs sm:text-sm">
                  <span className="text-slate-600">CAC:</span>
                  <span className="font-mono font-bold text-orange-700">€{a.cac.toFixed(2)}</span>
                  <span className="text-slate-500 text-[11px] sm:text-xs">(soglia €{a.threshold.toFixed(2)} · {a.appointments} appt · €{a.spend.toFixed(2)} spesa)</span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-1 h-6 text-[11px] border-red-300 text-red-700 hover:bg-red-50"
                    onClick={() => openAlertForCriticality(a.client_id, a.name, "cac", a)}
                    data-testid={`alert-cac-${i}`}
                  >
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Segnala
                  </Button>
                </div>
              </div>
            ))}
            {(cpanel.criticals?.views || []).map((a, i) => (
              <div key={"v-"+i} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 py-2 px-2 rounded-md bg-white/70 border border-purple-100" data-testid={`cp-views-${i}`}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <TrendingDown className="w-4 h-4 text-purple-600 flex-shrink-0" />
                  <span className="font-semibold text-purple-900 truncate">ATTENZIONE {a.name}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap text-xs sm:text-sm">
                  <span className="text-slate-600">Views calate:</span>
                  <span className="font-mono font-bold text-purple-700">-{Math.abs(a.delta_pct).toFixed(1)}%</span>
                  <span className="text-slate-500 text-[11px] sm:text-xs">({a.previous?.toLocaleString?.("it-IT") || a.previous} → {a.current?.toLocaleString?.("it-IT") || a.current} vs 30gg prec.)</span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-1 h-6 text-[11px] border-red-300 text-red-700 hover:bg-red-50"
                    onClick={() => openAlertForCriticality(a.client_id, a.name, "views", a)}
                    data-testid={`alert-views-${i}`}
                  >
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Segnala
                  </Button>
                </div>
              </div>
            ))}
            {(cpanel.criticals?.cpr || []).length === 0 &&
             (cpanel.criticals?.cac || []).length === 0 &&
             (cpanel.criticals?.views || []).length === 0 && (
              <div className="text-center py-4 text-emerald-700 text-sm">
                <CheckCircle2 className="w-5 h-5 inline mr-2" />
                Tutti i clienti sono nei parametri. Nessuna criticità.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Anchor per scroll dai tile */}
      <div id="tasks-list-anchor" />

      {/* Task section header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 pt-2 border-t border-slate-200">
        <div>
          <h2 className="font-heading text-xl font-bold text-foreground">Gestione Task</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {filteredTasks.length} task {hasActiveFilters ? "(filtrati)" : ""}
          </p>
        </div>
      </div>

      {/* Stats cards rimosse: task completati sono ora solo storico DB, non visibili nella lista.
          Le info di stato (Urgenti / In scadenza / Scaduti) sono già nei 3 tile del Controllo di Gestione. */}

      {/* Filters */}
      <Card className="border border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca cliente..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-tasks"
              />
            </div>

            {/* SMM Filter (PM only) */}
            {isPM && (
              <Select value={filterSmm} onValueChange={setFilterSmm}>
                <SelectTrigger className="w-full lg:w-48" data-testid="filter-smm">
                  <Users className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="SMM" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli SMM</SelectItem>
                  <SelectItem value="unassigned">Non assegnati</SelectItem>
                  {smmUsers.map(smm => (
                    <SelectItem key={smm.user_id} value={smm.user_id}>
                      {smm.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Status Filter */}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full lg:w-40" data-testid="filter-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="da_fare">Da fare</SelectItem>
                <SelectItem value="in_corso">In corso</SelectItem>
                <SelectItem value="completato">Completato</SelectItem>
              </SelectContent>
            </Select>

            {/* Task Type Filter */}
            <Select value={filterTaskType} onValueChange={setFilterTaskType}>
              <SelectTrigger className="w-full lg:w-48">
                <SelectValue placeholder="Tipo Task" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                {Object.entries(TASK_TYPE_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full lg:w-40">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Ordina" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="due_date">Per scadenza</SelectItem>
                <SelectItem value="semaforo">Per urgenza</SelectItem>
                <SelectItem value="client">Per cliente</SelectItem>
              </SelectContent>
            </Select>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* SMM Quick Filters (PM only) */}
      {isPM && smmUsers.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-sm text-muted-foreground self-center mr-2">Filtra per SMM:</span>
          {smmUsers.map(smm => {
            const smmTaskCount = tasks.filter(t => clientSmmMap[t.client_id] === smm.user_id && t.status !== "completato").length;
            return (
              <Badge
                key={smm.user_id}
                variant={filterSmm === smm.user_id ? "default" : "outline"}
                className={`cursor-pointer transition-all ${filterSmm === smm.user_id ? 'bg-indigo-600' : 'hover:bg-slate-100'}`}
                onClick={() => setFilterSmm(filterSmm === smm.user_id ? "all" : smm.user_id)}
                data-testid={`smm-badge-${smm.user_id}`}
              >
                {smm.name} ({smmTaskCount})
              </Badge>
            );
          })}
        </div>
      )}

      {/* Tasks List */}
      <Card className="border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Stato
                </th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Cliente
                </th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Task
                </th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Scadenza
                </th>
                {isPM && (
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    SMM
                  </th>
                )}
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedTasks.length === 0 ? (
                <tr>
                  <td colSpan={isPM ? 6 : 5} className="p-8 text-center text-muted-foreground">
                    Nessun task trovato con i filtri selezionati
                  </td>
                </tr>
              ) : (
                sortedTasks.map((task, index) => {
                  const semaforo = task.status === "completato" ? null : SEMAFORO_CONFIG[task.semaforo];
                  const SemaforoIcon = semaforo?.icon || CheckCircle2;
                  
                  return (
                    <tr 
                      key={task.task_id} 
                      className="border-b border-slate-100 table-row-hover"
                      data-testid={`task-row-${index}`}
                    >
                      <td className="p-4">
                        {task.status === "completato" ? (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Completato
                          </Badge>
                        ) : (
                          <Badge className={`${semaforo?.color} text-white`}>
                            <SemaforoIcon className="w-3 h-3 mr-1" />
                            {semaforo?.label}
                          </Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="font-medium">{task.client_name}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-muted-foreground">
                          {etichettaTask(task, TASK_TYPE_LABELS)}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm">
                          {new Date(task.due_date).toLocaleDateString("it-IT", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric"
                          })}
                        </span>
                      </td>
                      {isPM && (
                        <td className="p-4">
                          <span className="text-sm text-muted-foreground">
                            {getSmmName(clientSmmMap[task.client_id])}
                          </span>
                        </td>
                      )}
                      <td className="p-4">
                        <div className="flex gap-1">
                          {task.status === "da_fare" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(task.task_id, "in_corso")}
                              className="text-xs"
                            >
                              <Play className="w-3 h-3 mr-1" />
                              Inizia
                            </Button>
                          )}
                          {task.status === "in_corso" && (
                            <Button
                              size="sm"
                              onClick={() => handleStatusChange(task.task_id, "completato")}
                              className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Completa
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/task/${task.task_id}`)}
                            className="text-xs"
                          >
                            Dettagli
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setAlertContext({
                                clientId: task.client_id,
                                clientName: task.client_name || "",
                                source: "task",
                                sourceRef: task.task_id,
                                sourceLabel: `Task: ${etichettaTask(task, TASK_TYPE_LABELS)}`,
                                prefill: `Segnalo un problema sul task "${etichettaTask(task, TASK_TYPE_LABELS)}" (${task.client_name}):\n\n`,
                              });
                              setAlertOpen(true);
                            }}
                            className="text-xs text-red-600 hover:bg-red-50"
                            data-testid={`alert-task-${task.task_id}`}
                            title="Segnala un problema su questo task"
                          >
                            <AlertTriangle className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Alert Dialog condiviso per criticità / task */}
      <AlertRichiamoDialog
        open={alertOpen}
        onOpenChange={(v) => { setAlertOpen(v); if (!v) setAlertContext(null); }}
        clientId={alertContext?.clientId}
        clientName={alertContext?.clientName}
        source={alertContext?.source}
        sourceRef={alertContext?.sourceRef}
        sourceLabel={alertContext?.sourceLabel}
        prefillText={alertContext?.prefill}
      />
    </div>
  );
}
